export interface MoneyCounts {
  vendors: number;
  vendorsComplete: number;
  deadlinesOverdue: number;
  deadlinesSoon: number;
  giftCardRequests: number;
  treasuryRequestSent: boolean;
  salesReportSent: boolean;
}

export interface BudgetRow {
  bucket: string;
  label: string;
  direction: 'cost' | 'revenue';
  plannedCents: number;
  committedCents: number;
  actualCents: number;
  eReportsCents: number | null;
  notInEReportsCents: number | null;
  sources: { lines: number; vendors: number; ledger: number; eventbrite: number };
  missingAmounts: number;
}

export interface BudgetTotals {
  plannedCents: number;
  committedCents: number;
  actualCents: number;
  eReportsCents: number;
  notInEReportsCents: number;
}

export interface BudgetLine {
  id: string;
  bucket: string;
  label: string;
  plannedCents: number | null;
  committedCents: number | null;
  actualCents: number | null;
  note: string | null;
  archivedAt: string | null;
}

export interface Step {
  key: string;
  label: string;
  done: boolean;
  at: string | null;
  note?: string | null;
}

export interface Deadline {
  key: string;
  label: string;
  dueOn: string | null;
  doneAt: string | null;
  custom: boolean;
  state: 'done' | 'open' | 'soon' | 'overdue';
  vendorId?: string;
  vendor?: string;
}

export interface Charge {
  id: string;
  on: string;
  kind: string;
  rooms: number | null;
  nights: number | null;
  chargeCents: number | null;
  note: string | null;
}

export interface Vendor {
  id: string;
  vendorType: string;
  typeLabel: string;
  name: string;
  bucket: string;
  paymentMethod: string | null;
  pcardIncrease: boolean;
  costCentre: string | null;
  quoteCents: number | null;
  committedCents: number | null;
  actualCents: number | null;
  steps: Step[];
  next: string | null;
  complete: boolean;
  beoVersions: { label: string; date: string; note: string | null }[];
  deadlines: Deadline[];
  charges: Charge[];
  chargesCents: number;
  note: string | null;
  cancelledAt: string | null;
}

export interface GiftRequest {
  id: string;
  route: string;
  routeLabel: string;
  count: number;
  valueCents: number | null;
  totalCents: number | null;
  use: string | null;
  vendor: string | null;
  recipients: string | null;
  note: string | null;
  cancelledAt: string | null;
  steps: Step[];
  recordedInLedger: number;
}

export interface TicketType {
  name: string;
  priceCents: number | null;
  capacity: number | null;
}

export interface EventbriteRequest {
  salesOpen: string | null;
  salesClose: string | null;
  ticketTypes: TicketType[];
  expected: number | null;
  feesAbsorbed: boolean;
}

export interface EventMoney {
  visible: boolean;
  counts: MoneyCounts;
  buckets?: { cost: Record<string, string>; revenue: Record<string, string> };
  vendorTypes?: Record<string, string>;
  paymentMethods?: Record<string, string>;
  chargeKinds?: Record<string, string>;
  giftRoutes?: Record<string, string>;
  budget?: { rows: BudgetRow[]; totals: { cost: BudgetTotals; revenue: BudgetTotals }; missingAmounts: number };
  lines?: BudgetLine[];
  vendors?: Vendor[];
  deadlines?: Deadline[];
  giftCardRequests?: GiftRequest[];
  finance?: {
    costCentre: string | null;
    eReports: Record<string, number>;
    eReportsAsOf: string | null;
    eventbriteRequest: EventbriteRequest;
    treasury: { requestSentAt?: string; salesReportSentAt?: string };
  };
  eventbrite?: { grossCents: number | null; netCents: number | null; tickets: number | null } | null;
}

export interface RouteStep {
  key: string;
  label: string;
  prerequisite: boolean;
  optional: boolean;
  status: string | null;
  done: boolean;
  at: string | null;
  note: string | null;
}

export interface ClaimLine {
  type: string;
  amountCents: number | null;
  note: string | null;
}

export interface StepEntry {
  id: string;
  kind: string;
  status: string;
  amountCents: number | null;
  statusChangedAt: string;
  person: { name: string; email: string | null; type: string };
  route: string | null;
  routeLabel: string | null;
  lostReceipt: boolean;
  steps: RouteStep[];
  next: string | null;
  stuck: { step: string; days: number } | null;
  claimLines: ClaimLine[];
}

export interface EventSteps {
  counts: { total: number; withRoute: number; stuck: number; paid: number };
  namesVisible: boolean;
  routes?: { key: string; label: string }[];
  claimLineTypes?: Record<string, string>;
  defaultRoutes?: Record<string, string>;
  entries: StepEntry[];
}

export const moneyKey = (eventId: string) => ['event-money', eventId];

export const dateText = (iso: string | null | undefined) =>
  iso
    ? new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso).toLocaleDateString('en-CA', {
        timeZone: 'America/Toronto',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '—';
